<template>
	<v-form @submit.prevent="submit" :disabled="submitLoading">
		<template v-if="!permissions.items.isNone">
			<h2 class="text-h6 mb-2">
				{{$T('items-configs')}}
			</h2>
			<v-row dense>
				<!-- cardamom -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="cardamomRootId"
						:items="items.filter(c => c.type === type.main)"
						:item-text="item => item.itemNo + '-' + item.name"
						item-value="id"
						:label="$T('inputs.cardamom-root')"
						dense
						:disabled="permissions.items.isReadonly"
						outlined
						hide-details
					/>
				</v-col>
			
				<!-- raw cardamom -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="rawCardamom"
						:items="items.filter(c => c.type === type.sub)"
						:item-text="item => item.itemNo + ' - ' + item.name"
						item-value="id"
						:label="$T('inputs.raw-cardamom-item')"
						dense
						:disabled="permissions.items.isReadonly"
						outlined
						hide-details
					/>
				</v-col>

				<!-- crush -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="crushId"
						:items="flatCardamomItems"
						:item-text="item => item.itemNo + ' - ' + item.name"
						item-value="id"
						:label="$T('inputs.crush-item')"
						dense
						:disabled="permissions.items.isReadonly"
						outlined
						hide-details
					/>
				</v-col>

				<!-- seeds -->
				<v-col v-if="!isBlackPepper" lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="seedsId"
						:items="flatCardamomItems"
						:item-text="item => item.itemNo + ' - ' + item.name"
						item-value="id"
						:label="$T('inputs.seeds-item')"
						dense
						:disabled="permissions.items.isReadonly"
						outlined
						hide-details
					/>
				</v-col>
			</v-row>
		</template>

		<template v-if="!permissions.accounts.isNone">
			<h2 class="text-h6 my-2">
				{{$T('accounts-configs')}}
			</h2>
			<v-row dense>
				<!-- owner-tree -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="ownerRootId"
						:items="rootAccountsTrees"
						:item-text="item => item.accountingNo + ' - ' + item.name"
						item-value="id"
						:label="$T('inputs.owner-tree')"
						dense
						:disabled="permissions.accounts.isReadonly"
						outlined
						hide-details
					/>
				</v-col>
			
				<!-- providers-tree -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="providersRootId"
						:items="rootAccountsTrees"
						:item-text="item => item.accountingNo + '-' + item.name"
						item-value="id"
						:label="$T('inputs.providers-tree')"
						dense
						:disabled="permissions.accounts.isReadonly"
						outlined
						hide-details
					/>
				</v-col>
			
				<!-- customers-tree -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="customersRootId"
						:items="rootAccountsTrees"
						:item-text="item => item.accountingNo + ' - ' + item.name"
						item-value="id"
						:label="$T('inputs.customers-tree')"
						dense
						:disabled="permissions.accounts.isReadonly"
						outlined
						hide-details
					/>
				</v-col>

				<!-- detachment -->
				<v-col lg="3" md="4" sm="6" cols="12">
					<v-autocomplete
						v-model="detachmentId"
						:items="accounts.filter(c => c.accountType === type.sub)"
						:item-text="item => item.accountingNo + ' - ' + item.name"
						item-value="id"
						:label="$T('inputs.detachment')"
						dense
						:disabled="permissions.accounts.isReadonly"
						outlined
						hide-details
					/>
				</v-col>
			
				<v-col
					v-if="permissions.items.isReadWrite || permissions.accounts.isReadWrite"
					cols="12"
					class="pt-4"
				>
					<v-btn color="primary" type="submit">
						{{$T('save')}}
					</v-btn>
				</v-col>
			</v-row>
		</template>
	</v-form>
</template>

<script>
import { type, configs } from '@/helpers/enums'
import { mapState } from 'vuex'
import { getFlatTree } from '@/helpers/functions'
import { isBlackPepper } from '@/global.config'	

export default {
	name: 'GeneralSettings',
	data() {
		return {
			submitLoading: false,
			type,

			// data
			ownerRootId: null,
			providersRootId: null,
			customersRootId: null,
			detachmentId: null,

			rawCardamom: null,
			cardamomRootId: null,
			crushId: null,
			seedsId: null,
			isBlackPepper
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			accounts: state => state.accounts.accounts.filter(c => !c.isArchived),
			items: state => state.items.items,
			itemsTree: state => state.items.itemsTree,
			itemsConfigs: state => state.configs.itemsConfigs,
			accountsConfigs: state => state.configs.accountsConfigs,
		}),

		rootAccountsTrees() {
			return this.accounts.filter(c => c.accountType === type.main);
		},

		flatCardamomItems() {
			if (this.itemsConfigs.cardamomRootId) {
				return getFlatTree([this.itemsTree.find(c => c.id === this.itemsConfigs.cardamomRootId)]);
			} else {
				return this.items.filter(c => c.type === type.sub);
			}
		}
	},

	methods: {
		submit() {
			this.submitLoading = true;
			const promises = [];

			if (this.cardamomRootId && this.cardamomRootId !== this.itemsConfigs.cardamomRootId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.cardamomRootId,
					configType: configs.cardamomRoot,
				}))
			};
			if (this.rawCardamom && this.rawCardamom !== this.itemsConfigs.rawCardamomId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.rawCardamom,
					configType: configs.rawCardamom,
				}))
			};
			if (this.crushId && this.crushId !== this.itemsConfigs.crushId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.crushId,
					configType: configs.crush,
				}))
			};
			if (this.seedsId && this.seedsId !== this.itemsConfigs.seedsId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.seedsId,
					configType: configs.seeds,
				}))
			};
			if (this.ownerRootId && this.ownerRootId !== this.accountsConfigs.ownersRootId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.ownerRootId,
					configType: configs.owners,
				}))
			};
			if (this.providersRootId && this.providersRootId !== this.accountsConfigs.providersRootId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.providersRootId,
					configType: configs.providers,
				}))
			};
			if (this.customersRootId && this.customersRootId !== this.accountsConfigs.customersRootId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.customersRootId,
					configType: configs.customers,
				}))
			};
			if (this.detachmentId && this.detachmentId !== this.accountsConfigs.detachmentId) {
				promises.push(this.$store.dispatch('configs/update', {
					id: this.detachmentId,
					configType: configs.detachment,
				}))
			};
			if (promises.length) {
				promises.push(this.$store.dispatch('configs/fetchAll'));
				promises.push(this.$store.dispatch('configs/fetchConfigAccounts'));
			}

			Promise.all(promises).finally(() => {
				this.submitLoading = false;
			})
		}
	},
	
	created() {
		this.cardamomRootId = this.itemsConfigs.cardamomRootId;
		this.rawCardamom = this.itemsConfigs.rawCardamomId;
		this.crushId = this.itemsConfigs.crushId;
		this.seedsId = this.itemsConfigs.seedsId;

		this.ownerRootId = this.accountsConfigs.ownersRootId;
		this.providersRootId = this.accountsConfigs.providersRootId;
		this.customersRootId = this.accountsConfigs.customersRootId;
		this.detachmentId = this.accountsConfigs.detachmentId;
	}
}
</script>

<style>

</style>